import React, { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ShopContext } from '../../Context/ShopContext';
import { clImg } from '../../utils/cloudinary';
import './Search.css';

const MAX_RESULTS = 6;
const HISTORY_KEY = 'forge-search-history';
const MAX_HISTORY = 8;

const loadHistory = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const Search = ({ onClose }) => {
  const [query, setQuery]     = useState('');
  const [history, setHistory] = useState(loadHistory);
  const inputRef               = useRef();
  const navigate                = useNavigate();
  const { products }            = useContext(ShopContext);

  // Auto-focus when overlay opens
  useEffect(() => { inputRef.current?.focus(); }, []);

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const results = q
    ? (products || []).filter(item =>
        (item.name        || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        (item.category     || "").toLowerCase().includes(q)
      )
    : [];

  const persistHistory = (list) => {
    setHistory(list);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch {}
  };

  const addToHistory = (item) => {
    const entry = { id: item.id, name: item.name, image: item.image };
    const next = [entry, ...history.filter(h => h.id !== item.id)].slice(0, MAX_HISTORY);
    persistHistory(next);
  };

  const removeFromHistory = (id, e) => {
    e.stopPropagation();
    persistHistory(history.filter(h => h.id !== id));
  };

  const clearHistory = () => persistHistory([]);

  const goToProduct = (item) => {
    addToHistory(item);
    onClose();
    navigate(`/product/${item.id}`);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q) return;
    onClose();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  return createPortal(
    <div className="search-overlay" onClick={onClose}>
      <div className="search-box" onClick={e => e.stopPropagation()}>
        <form className="search-input-wrap" onSubmit={submitSearch}>
          <svg className="search-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search products…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="search-clear" onClick={() => setQuery('')} aria-label="Clear">✕</button>
          )}
          <button type="button" className="search-close-btn" onClick={onClose} aria-label="Close search">ESC</button>
        </form>

        {!q && history.length > 0 && (
          <div className="search-history">
            <div className="search-history-head">
              <span>Recently Viewed</span>
              <button type="button" className="search-history-clear" onClick={clearHistory}>Clear</button>
            </div>
            <div className="search-history-chips">
              {history.map(item => (
                <button key={item.id} type="button" className="search-history-chip" onClick={() => goToProduct(item)}>
                  <img src={clImg(item.image, { width: 60 })} alt="" className="search-history-img" loading="lazy" decoding="async" />
                  <span>{item.name}</span>
                  <span className="search-history-remove" onClick={(e) => removeFromHistory(item.id, e)}>✕</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {q && (
          <div className="search-results">
            {results.length > 0 ? (
              <>
                {results.slice(0, MAX_RESULTS).map(item => (
                  <button
                    key={item.id}
                    type="button"
                    className="search-result-row"
                    onClick={() => goToProduct(item)}
                  >
                    <img src={clImg(item.image, { width: 80 })} alt="" className="search-result-img" loading="lazy" decoding="async" />
                    <span className="search-result-info">
                      <span className="search-result-name">{item.name}</span>
                      <span className="search-result-price">₹{item.new_price}</span>
                    </span>
                  </button>
                ))}
                <button type="button" className="search-view-all" onClick={submitSearch}>
                  View all results for "{query.trim()}"
                </button>
              </>
            ) : (
              <p className="search-no-results">No products found for "{query.trim()}"</p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Search;
